import { DefaultResponseWithData } from "../system";

// Mirrors GPMetals.Core.Models.ListingOptimisation.IntelligencePricingOutcome. The API serialises
// enums as numbers; normaliseIntelligencePricingOutcome also accepts the names in case that changes.
export enum IntelligencePricingOutcome {
  Priced = 0,
  Held = 1,
  NoData = 2,
  Unchanged = 3,
}

const OUTCOME_NAMES: Record<string, IntelligencePricingOutcome> = {
  priced: IntelligencePricingOutcome.Priced,
  held: IntelligencePricingOutcome.Held,
  nodata: IntelligencePricingOutcome.NoData,
  unchanged: IntelligencePricingOutcome.Unchanged,
};

export function normaliseIntelligencePricingOutcome(
  value: IntelligencePricingOutcome | number | string | null | undefined
): IntelligencePricingOutcome {
  if (typeof value === "number") return value as IntelligencePricingOutcome;
  if (typeof value === "string") {
    const byName = OUTCOME_NAMES[value.trim().toLowerCase()];
    if (byName !== undefined) return byName;
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) return asNumber as IntelligencePricingOutcome;
  }
  return IntelligencePricingOutcome.NoData;
}

// Mirrors GPMetals.Core.Models.ListingOptimisation.IntelligencePricingReviewResult - one part's
// outcome from POST /listingoptimisation/price-from-intelligence.
export interface IIntelligencePricingReviewResult {
  partId: string;
  tagNumber: string | null;
  outcome: IntelligencePricingOutcome | string;
  reason: string | null;
  livePrice: number | null;
  stagedPrice: number | null;
  average: number | null;
  foundCount: number;
  keptCount: number;
}

// Mirrors GPMetals.Core.Models.ListingOptimisation.AiTitleStageResult - one part's outcome from
// POST /listingoptimisation/ai-titles. Titles over 80 characters or with warnings come back unstaged.
export interface IAiTitleStageResult {
  partId: string;
  tagNumber: string | null;
  title: string | null;
  characterCount: number;
  score: number;
  warnings: string[];
  staged: boolean;
  reason: string | null;
}

// Mirrors GPMetals.Core.Models.Intelligence.IntelligenceReviewListing.
export interface IIntelligenceReviewListing {
  title: string | null;
  link: string | null;
  condition: string | null;
  price: number | null;
  thumbnail: string | null;
  isSold: boolean;
  /** Set when the AI filter judged this listing not to be the part. Null = counted. */
  excludedReason: string | null;
}

// Mirrors GPMetals.Core.Models.Intelligence.PartIntelligenceReviewResponse - the latest lookup
// for a part, from GET /listingoptimisation/intelligence-review/{partId}.
export interface IPartIntelligenceReview {
  partId: string;
  lookedUpOn: string;
  searchTerm: string | null;
  aiFilterApplied: boolean;
  foundCount: number;
  keptCount: number;
  min: number | null;
  average: number | null;
  max: number | null;
  listings: IIntelligenceReviewListing[];
}

export interface IListingOptimisationPartsRequest {
  partDataIds: string[];
}

export interface IIntelligencePricingReviewResponse
  extends DefaultResponseWithData<IIntelligencePricingReviewResult[]> {}
export interface IAiTitleStageResponse extends DefaultResponseWithData<IAiTitleStageResult[]> {}
export interface IPartIntelligenceReviewResponse extends DefaultResponseWithData<IPartIntelligenceReview> {}
export interface IListingReviewedResponse extends DefaultResponseWithData<number> {}
